import { NgClass, NgStyle } from '@angular/common';
import {

  afterNextRender, ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, signal, viewChild
} from '@angular/core';
import { YouTubePlayer } from '@angular/youtube-player';

import { Subscription } from 'rxjs';

import { Clock } from '../../../runtime/clock/clock';
import { PresentationController } from '../../presentation-controller';
import { VideoErrorComponent } from '../video-error/video-error.component';

const START_DELAY_MS = 2000;
const POLL_MS = 1000;
const NUDGE_EVERY_POLLS = 4;
const SECONDS_PER_RETRY = 5;
// The YT namespace only exists once the iframe API has loaded, so the states we compare against are inlined.
const PLAYER_UNSTARTED = -1;
const PLAYER_ENDED = 0;
const PLAYER_PLAYING = 1;
const ERROR_CARD_MS = 5000;

@Component({
  selector: 'ds-video-player',
  imports: [NgClass, NgStyle, YouTubePlayer, VideoErrorComponent],
  templateUrl: './video-player.component.html',
  styleUrls: ['./video-player.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VideoPlayerComponent {
  private readonly clock = inject(Clock);
  private readonly destroyRef = inject(DestroyRef);
  private readonly subscriptions = new Subscription();
  private readonly player = viewChild<YouTubePlayer>('player');
  private state?: number;
  private done = false;

  public readonly videoId = input.required<string>();
  public readonly controller = input.required<PresentationController>();
  public readonly thumbnailUrl = input('');
  public readonly fullscreen = input(false);
  public readonly overlay = input(false);
  public readonly closedCaptions = input(false);
  public readonly retryLimit = input(20);
  public readonly autoplay = input(true);
  public readonly showThumbnail = signal(true);
  public readonly showSpinner = signal(false);
  public readonly unplayable = signal(false);

  public readonly playerVars = computed<YT.PlayerVars>(() => ({
    controls: 0, disablekb: 1, fs: 0, iv_load_policy: 3, modestbranding: 1, playsinline: 1, rel: 0,
    ...(this.closedCaptions() ? { cc_load_policy: 1 } : {})
  }));

  public constructor() {
    afterNextRender(() => this.begin());
    this.destroyRef.onDestroy(() => this.subscriptions.unsubscribe());
  }

  private begin(): void {
    const player = this.player();
    const controller = this.controller();

    if (!this.autoplay()) {
      return;
    }

    if (!player) {
      this.finish(true);

      return;
    }

    this.applyFrameStyles(player);
    this.subscriptions.add(player.stateChange.subscribe(event => this.onState(event.data)));
    this.subscriptions.add(player.error.subscribe(() => { this.state = PLAYER_UNSTARTED; }));
    void this.run(player, controller.signal).catch((): void => undefined);
  }

  // The player is polled rather than driven purely by events: a YouTube frame can stop emitting entirely.
  private async run(player: YouTubePlayer, signal: AbortSignal): Promise<void> {
    await this.clock.after(START_DELAY_MS, signal);
    this.showSpinner.set(true);
    player.playVideo();
    const limit = Math.max(1, this.retryLimit() * SECONDS_PER_RETRY);
    let stalled = 0;

    while (!signal.aborted && !this.done) {
      await this.clock.after(POLL_MS, signal);

      if (this.state === PLAYER_PLAYING) {
        stalled = 0;
        continue;
      }

      stalled++;

      if (stalled % NUDGE_EVERY_POLLS === 0) {
        player.playVideo();
      }

      if (stalled >= limit) {
        this.finish(true);

        return;
      }
    }
  }

  private onState(state: number): void {
    this.state = state;

    if (state === PLAYER_PLAYING) {
      this.showThumbnail.set(false);
      this.showSpinner.set(false);
    }

    if (state === PLAYER_ENDED) {
      this.showThumbnail.set(true);
      this.finish(false);
    }
  }

  private finish(failed: boolean): void {
    if (this.done) {
      return;
    }

    this.done = true;
    this.showSpinner.set(false);
    const controller = this.controller();

    if (!failed) {
      controller.mediaEnded();

      return;
    }

    // The retry ladder is spent: say so on screen for a few seconds rather than cutting straight to the next slide.
    this.showThumbnail.set(true);
    this.unplayable.set(true);
    controller.fail();
    void this.clock.after(ERROR_CARD_MS, controller.signal)
      .then((): void => controller.mediaEnded(), (): void => undefined);
  }

  private applyFrameStyles(player: YouTubePlayer): void {
    // The player component offers no style hook for the iframe it creates.
    const frame = player.youtubeContainer.nativeElement as HTMLElement;
    frame.style.position = 'absolute';
    frame.style.top = '0';
    frame.style.left = '0';
    frame.style.width = '100%';
    frame.style.height = '100%';

    if (!this.fullscreen()) {
      frame.style.borderRadius = '10px';
    }
  }
}
